import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useAuthSessionAnalytics } from '@/hooks/auth-session-analytics.hook';
import { authResultSearchSchema } from '@/services/auth-service';

/**
 * Pathless layout for the paid-traffic funnel onboarding (`/onboarding/$step`).
 *
 * Lives OUTSIDE `_marketing` and `_authenticated` so the funnel controls its
 * own analytics context. Staying out of `_marketing` is deliberate: its
 * `head()` injects the landing-stage pixels (TikTok/Quora `ViewContent` and
 * friends), and firing those on mid-funnel steps is the duplicate-event bug
 * Phase 1 of `docs/projects/complete/accounts-and-billing/audit-cookie-trackers.md` found on
 * `/post-purchase`. The landing page itself DOES sit under `_marketing` —
 * that is where those pixels belong, and both experiment arms need them
 * identically. The site-wide pixels (Meta PageView, the Google tag, Reddit,
 * Bing, LinkedIn, Clarity) are mounted on `__root.tsx`, so every funnel step
 * still boots them once per document.
 *
 * Session replay is project-level and server-controlled — sampling lives in
 * `packages/infra/src/posthog/definitions/team-settings.ts` — so the funnel neither starts nor stops
 * recording itself.
 */
export const Route = createFileRoute('/_funnel')({
  // A social signup from the funnel's signup step lands back INSIDE the funnel
  // (`/onboarding/paywall`), so the auth markers arrive here rather than on
  // `/app/_authenticated`. Declared on the layout, not the step route, so it
  // holds wherever a future flow chooses to land.
  validateSearch: authResultSearchSchema,
  component: FunnelLayout,
});

function FunnelLayout() {
  // Identity + `auth_success` for a social signup that landed in the funnel.
  // The app mounts this on `_authenticated`; a funnel landing never passes
  // through that layout, so without this copy those users would keep their
  // anonymous distinct_id and their sign-in would go unreported — which is
  // also what ties the purchase that follows to the experiment arm.
  useAuthSessionAnalytics({
    authResult: Route.useSearch({ select: (s) => s.auth_result }),
    authProvider: Route.useSearch({ select: (s) => s.auth_provider }),
  });

  return <Outlet />;
}
